import React from 'react';
import {
    DropdownItem,
    DropdownV2,
    Flex,
    Icon,
    PriorityIcon,
    useDropdown,
    Text,
    noop,
    useKeyboardNavigation
} from '@priority-software/priority-style-react';
import {HomepageDialog, homepageStore} from '../../store/HomepageStore';
import {openNewGroupDialog} from '../../actions/dialogs';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {DefaultGroupsIDs} from '../../enums';
import {SettingsDropdownAction} from './enums';
import {groupOptions, insightsGroupOptions} from './constants';
import texts from '../../texts';
import {ignoreDragClassName} from '../../constants';
import {createDataTestId} from '../../../../utils/dataTestId';

type Props = {
    groupTitle: string;
    disableDelete?: boolean;
    componentClasses?: string;
    isPortletGroup?: boolean;
};

export const GroupSettingsDropdown = (props: Props) => {
    const {groupTitle, disableDelete, componentClasses, isPortletGroup} = props;

    const {isOpen, toggleOpen, closeDropdown} = useDropdown({onClose: noop, onOpen: noop});
    const {openDialog, setSelectedGroup} = homepageStore;
    const options = isPortletGroup ? insightsGroupOptions : groupOptions;

    const onOptionSelect = (option: any) => {
        if (option.value === SettingsDropdownAction.remove_group) {
            if (disableDelete) return;

            setSelectedGroup(isPortletGroup ? DefaultGroupsIDs.insights : groupTitle);
            openDialog(HomepageDialog.removeGroup, true);
        } else if (option.value === SettingsDropdownAction.edit_group_name) {
            openDialog(HomepageDialog.addGroup, true);
            setSelectedGroup(groupTitle);
        } else if (option.value === SettingsDropdownAction.add_new_group) {
            openNewGroupDialog();
        }

        closeDropdown();
    };

    const handleEnter = (e: KeyboardEvent, focusedIndex: number) => {
        const option = options[focusedIndex];
        if (option) {
            onOptionSelect(option);
        }
        closeDropdown();
    };

    const {focusedIndex, handleNavigationKeys} = useKeyboardNavigation({
        isOpen,
        defaultFocusedIndex: 0,
        handleEnter,
        optionsCount: options.length
    });

    const getTextColor = (value: SettingsDropdownAction) => {
        if (value === SettingsDropdownAction.remove_group && disableDelete) {
            return 'secondary-200';
        }
        return 'primary-700';
    };

    return (
        <DropdownV2
            isOpen={isOpen}
            trigger={<Icon onClick={toggleOpen} icon={PriorityIcon.threeDots} />}
            componentClasses={componentClasses}
            contentComponentClasses={ignoreDragClassName}
            onClose={closeDropdown}
            onKeyDown={handleNavigationKeys}
            contentStyle={{width: 220}}
            eventTypes={['touchstart', 'mousedown']}
            openingSide="leftSide"
            usePortal
            zIndex={10}
        >
            <div {...createDataTestId(`hp-group-settings-dropdown`)}>
                {options.map((option, index) => (
                    <DropdownItem
                        key={option.value}
                        data={option}
                        onSelect={onOptionSelect}
                        isFocused={focusedIndex === index}
                        contentRenderer={item => {
                            const textColor = getTextColor(item.value as SettingsDropdownAction);
                            const isDisabledDelete =
                                SettingsDropdownAction.remove_group && disableDelete;
                            const cursor =
                                item.value === isDisabledDelete ? 'not-allowed' : 'pointer';

                            const optionEl = (
                                <Flex
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    style={{gap: 10, cursor}}
                                >
                                    <Icon icon={item.icon} iconColor={textColor} />

                                    <Text skin="paragraph-2" textColor={textColor}>
                                        {item.label}
                                    </Text>
                                </Flex>
                            );
                            return isDisabledDelete ? (
                                <ModernTooltip
                                    tooltip={texts.delete_group_label_tooltip}
                                    position="top"
                                    delay={500}
                                >
                                    {optionEl}
                                </ModernTooltip>
                            ) : (
                                optionEl
                            );
                        }}
                    />
                ))}
            </div>
        </DropdownV2>
    );
};
